import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PhotoUploader from "@/components/PhotoUploader";
import { Lightbulb, Bug, Send, Paperclip } from "lucide-react";

/**
 * SuggestionDialog — "Ideas / Issues" form for hubCMD team.
 * Submissions email support@trupathadvisory.com only (not the org's Controller).
 * Supports photo & file attachments (screenshots etc.).
 */
export default function SuggestionDialog({ open, onClose }) {
  const [category, setCategory] = useState("idea");
  const [message, setMessage] = useState("");
  const [fileIds, setFileIds] = useState([]);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setMessage(""); setCategory("idea"); setFileIds([]);
    onClose?.();
  };

  const submit = async () => {
    if (!message.trim()) return toast.error("Please describe your idea or issue.");
    setBusy(true);
    try {
      await api.post("/feedback", {
        category,
        message: message.trim(),
        page: window.location.pathname,
        file_ids: fileIds,
      });
      toast.success("Thanks! Sent to the hubCMD team.");
      reset();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && reset()}>
      <DialogContent className="max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-lg flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-[#ff4500]" /> Send us an idea or report an issue
          </DialogTitle>
        </DialogHeader>
        <p className="text-[12px] text-[#1a1a1a]/60 -mt-1">
          Goes directly to the hubCMD team at TruPath Advisory.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] font-mono text-[#1a1a1a]/65 mb-1.5">
              Category
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                ["idea", "Idea", Lightbulb, "#d6a900", "Feature request or improvement"],
                ["issue", "Issue", Bug, "#ff4500", "Something isn't working right"],
              ].map(([k, l, Icon, c, sub]) => (
                <button key={k} type="button" data-testid={`sugg-cat-${k}`} onClick={() => setCategory(k)}
                  className={`border-2 rounded-xl p-3 text-left transition-colors ${
                    category === k ? "border-[#ff4500] bg-[#fff1ea]" : "border-[#1a1a1a]/10 hover:border-[#1a1a1a]/30"
                  }`}>
                  <Icon className="w-4 h-4 mb-1" style={{ color: c }} />
                  <div className="text-[13px] font-display font-bold text-[#1a1a1a]">{l}</div>
                  <div className="text-[10px] text-[#1a1a1a]/55 font-mono uppercase tracking-wider">{sub}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] font-mono text-[#1a1a1a]/65 mb-1.5">
              {category === "idea" ? "Tell us your idea" : "Describe the issue"}
            </label>
            <textarea data-testid="sugg-message" rows={5}
              value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder={category === "idea"
                ? "What would make hubCMD work better for you? Be as specific as possible."
                : "What happened? What did you expect? Steps to reproduce."}
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#1a1a1a] focus:ring-2 focus:ring-[#1a1a1a]/15" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] font-mono text-[#1a1a1a]/65 mb-1.5">
              <Paperclip className="w-3 h-3 inline mr-1" /> Attach a screenshot or file (optional)
            </label>
            <PhotoUploader photoIds={fileIds} onChange={setFileIds} testIdPrefix="sugg-file" />
            <div className="text-[10px] text-[#1a1a1a]/55 mt-1 font-mono">
              Screenshots make issues 10× easier to fix.
            </div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={reset} className="btn-pill btn-pill-ghost text-[11px]">Cancel</button>
          <button data-testid="sugg-submit" onClick={submit} disabled={busy}
            className="btn-pill btn-pill-primary text-[11px]">
            <Send className="w-3 h-3" /> {busy ? "Sending…" : "Send to hubCMD team"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
